'use client';

import { useState, useEffect } from 'react';
import { decodeQrLocationFromImageData } from '@/lib/qr/decode';
import { currentScanResult, type DecodedScan } from '../utils/scan-result';

interface QrScanState {
  qrScanning: boolean;
  qrScanResult: 'match' | 'mismatch' | null;
  scanMismatch: { expected: string; scanned: string } | null;
  scannedQrValue: string | null;
  /** Corners of the printed QR in 0..1 fractions of the natural image, or null
   *  when no QR was decoded. Fed straight to <QrOverlay liveCorners=…/> as the
   *  fallback path for pages without a persisted bbox. */
  liveQrCorners: { x: number; y: number }[] | null;
  scanTrigger: number;
  triggerRescan: () => void;
  resetScanState: () => void;
}

export function useQrScanFromImage(
  imageBlobUrl: string | null,
  expectedQr: string | null
): QrScanState {
  // One decode, tagged with the image + expected code it was read for.
  // Everything returned is derived through `currentScanResult`, so a page
  // change hides the previous page's result in the SAME render — the backfill
  // effect runs in that commit, before this hook's effect can clear anything,
  // and used to save the old page's QR onto the new page.
  const [scan, setScan] = useState<DecodedScan | null>(null);
  const [scanTrigger, setScanTrigger] = useState(0);

  const triggerRescan = () => setScanTrigger((n) => n + 1);

  const resetScanState = () => setScan(null);

  useEffect(() => {
    if (!imageBlobUrl || !expectedQr) return;

    let cancelled = false;
    const record = (
      value: string | null,
      corners: { x: number; y: number }[] | null
    ) => {
      if (!cancelled) setScan({ imageBlobUrl, expectedQr, value, corners });
    };

    // Use <img> + canvas instead of fetch(blobUrl) so we don't tip over when a
    // browser extension intercepts fetch() of blob URLs (one such extension
    // surfaces as `chrome-extension://mdnleldcmiljblolnjhpnblkcekpdkpa`).
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = imageBlobUrl;
    img.onload = () => {
      if (cancelled) return;
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        record(null, null);
        return;
      }
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = decodeQrLocationFromImageData(
        imageData,
        canvas.width,
        canvas.height
      );
      record(result?.data ?? null, result?.corners ?? null);
    };
    // An unreadable image is a finished scan with nothing found, not a
    // scan that never ends.
    img.onerror = () => record(null, null);

    return () => {
      cancelled = true;
    };
  }, [imageBlobUrl, expectedQr, scanTrigger]);

  // A rescan of the same image keeps the previous result until the new decode
  // lands; the tag only changes when the page or image does.
  const current = currentScanResult(scan, imageBlobUrl, expectedQr);

  return {
    qrScanning: current.pending,
    qrScanResult: current.result,
    scanMismatch:
      current.result === 'mismatch' && expectedQr && current.value
        ? { expected: expectedQr, scanned: current.value }
        : null,
    scannedQrValue: current.value,
    liveQrCorners: current.corners,
    scanTrigger,
    triggerRescan,
    resetScanState
  };
}
